import React from "react";
import { MemberImage as MemberImageType } from "../../../types/membersTypes";
import JBTLogoBlack from "../../../assets/jbt-logo-black.png";
import { Avatar, Box, Divider, Paper, Typography, useTheme } from "@mui/material";
import {
  AccessTimeOutlined,
  CakeOutlined,
  EmailOutlined,
  FemaleOutlined,
  MaleOutlined,
  PhoneAndroidOutlined,
  Transgender,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { formatPhoneNumber } from "../../../utils/phoneUtils";

interface PersonalInformationProps {
  firstName: string;
  lastName: string;
  gender: string | null;
  birthDate: Date | null;
  email: string;
  enrolledDegree: string;
  mobilePhone: string;
  image: MemberImageType | null;
  applicationDate: Date | null;
}

/**
 * This component displays the personal information of an applicant
 * @param firstName The first name of the applicant
 * @param lastName The last name of the applicant
 * @param gender The gender of the applicant
 * @param birthDate The birth date of the applicant
 * @param email The email of the applicant
 * @param enrolledDegree The enrolled degree of the applicant
 * @param mobilePhone The mobile phone number of the applicant
 * @param image The image of the applicant
 * @returns The PersonalInformation component
 */
const PersonalInformation = ({
  firstName,
  lastName,
  gender,
  birthDate,
  email,
  enrolledDegree,
  mobilePhone,
  image,
  applicationDate,
}: PersonalInformationProps) => {
  const theme = useTheme();

  // Function to check the email length and split it into two lines if it is too long
  const checkEmailAndFormat = (email: string): string => {
    // Check if the email is too long and split it into two lines before the @ symbol
    if (email.length > 35) {
      const atIndex = email.indexOf("@");
      if (atIndex > 0) {
        return email.slice(0, atIndex) + "\n" + email.slice(atIndex);
      }
    }
    return email;
  };

  // Function to calculate the age based on the birth date
  const calcAge = (date: Date | null): string => {
    if (!date) {
      return "";
    }
    return dayjs().diff(dayjs(date), "year").toString();
  };

  const formattedPhoneNumber = formatPhoneNumber(mobilePhone);
  const formattedEmail = checkEmailAndFormat(email);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: 1,
        borderColor: "#C4c4c4",
        borderRadius: 2,
        padding: 2,
      }}
      elevation={1}
    >
      <Avatar
        src={image?.base64 ? `data:image/${image?.mimeType};base64,${image?.base64}` : JBTLogoBlack}
        alt={"ApplicantImage"}
        sx={{
          objectFit: "cover",
          borderRadius: "50%",
          border: "3px solid var(--white,#fff)",
          boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.14)",
          width: 250,
          height: 250,
        }}
      />
      <Typography fontWeight={"bold"} fontSize={26} sx={{ marginTop: 1 }}>
        {firstName} {lastName}
      </Typography>
      <Typography fontSize={18} color={theme.palette.secondary.main}>
        {enrolledDegree}
      </Typography>
      <Divider sx={{ width: "90%", marginTop: 2 }} />
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", direction: "row", alignItems: "center" }}>
          <MaleOutlined fontSize="small" color="info" />
          <Typography fontSize={26}> / </Typography>
          <FemaleOutlined fontSize="small" color="info" />
          <Typography fontSize={26}> / </Typography>
          <Transgender fontSize="small" color="info" sx={{ marginLeft: 0.5 }} />
        </Box>
        <Typography>{gender}</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginBottom: 1,
        }}
      >
        <CakeOutlined fontSize="small" color="info" />
        <Typography>
          {dayjs(birthDate).format("DD.MM.YYYY")} ({calcAge(birthDate)})
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginBottom: 1,
        }}
      >
        <PhoneAndroidOutlined fontSize="small" color="info" />
        <Typography>{formattedPhoneNumber}</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginBottom: 1,
        }}
      >
        <EmailOutlined fontSize="small" color="info" />
        <Typography>
          {formattedEmail.split("\n").map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index !== formattedEmail.split("\n").length - 1 && <br />}
            </React.Fragment>
          ))}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          direction: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <AccessTimeOutlined fontSize="small" color="info" />
        <Typography>{dayjs(applicationDate).format("DD.MM.YYYY HH:mm")}</Typography>
      </Box>
    </Paper>
  );
};

export default PersonalInformation;
